import { CommonModule, formatDate } from "@angular/common";
import { Component, Inject, InjectionToken, Injector, OnInit, PLATFORM_ID, signal } from "@angular/core";
import { ButtonModule } from "primeng/button";
import { ProgressBarModule } from "primeng/progressbar";
import { TableModule } from "primeng/table";
import { CBoxAdminLockerCreateConfirmDialogComponent } from "../dialogs/create/cbox-admin-locker-create-confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { filter, first, tap } from "rxjs";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ProfileService } from "src/services/profile/profile.service";
import { formatBytes, formatTimeInMilliseconds, hasMinutesPassedSince } from "src/helpers/functions";
import { TagModule } from "primeng/tag";
import { CBoxAdminLockerConfigDialogComponent } from "../dialogs/config/cbox-admin-locker-config-dialog.component";
import { MultiSelectModule } from "primeng/multiselect";
import { CBox_AdminLockerData, CBox_GetAdminLockerListDataItem } from "@server/services/cbox/public/api/v1/resources/internal/locker/types";
import { CheckboxModule } from "primeng/checkbox";
import { CBoxAdminLockerInfoComponent } from "../dialogs/info/cbox-admin-locker-info.component";
import { PaginatedTableWithFilters } from "app/shared/paginated-table-with-filters";
import { CBox_GetLockerListDataParams } from "@server/services/cbox/public/api/v1/resources/locker/types";

type ExtendedDataTypeItem = CBox_GetAdminLockerListDataItem & {
  isOldHeartbeat: boolean;
  storageFreePercentage: number;
  memoryFreePercentage: number;
}

@Component({
  selector: "app-cbox-admin-lockers-list",
  templateUrl: "./cbox-admin-lockers-list.component.html",
  styleUrls: ["./cbox-admin-lockers-list.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    ProgressBarModule,
    TagModule,
    ReactiveFormsModule,
    MultiSelectModule,
    CheckboxModule
  ]
})

export class CBoxAdminLockersListComponent extends PaginatedTableWithFilters<CBox_GetAdminLockerListDataItem, CBox_GetLockerListDataParams> implements OnInit {

  filtersForm: FormGroup;
  sessionFiltersKey = "lockers/admin/list";
  localStorageFiltersKey = "lockers/admin/list";
  sessionFiltersToSave = [
    "page",
    "isActive",
    "pageSize",
    "isConfigured",
    "lockerIdentifiers",
    "includePickupCount",
    "includeDropoffCount",
  ];
  localStorageFiltersToSave = [];
  dataFetchUrl = "internal/lockers";
  defaultFilters = signal({
    lockerIdentifiers: [],
    pageSize: 100,
    page: 1,
    includePickupCount: false,
    includeDropoffCount: false,
    isActive: false,
    isConfigured: false
  });
  lockerExtras = signal<Record<string, any>>({});

  refreshInterval?: NodeJS.Timeout;
  autoRefresh = new FormControl(false);

  scrollPosition = signal(0);

  constructor(
    inj: Injector,
    private profile: ProfileService,
    private dialog: MatDialog,
    private fb: FormBuilder,
  ) {
      super(inj);
      this.profile.setTitle("Listă lockere admin");
      this.filtersForm = this.fb.group({
        lockerIdentifiers: [[]],
        pageSize: [100], // default 100
        page: [1], // default 1
        includePickupCount: [false],
        includeDropoffCount: [false],
        isActive: [false],
        isConfigured: [false]
      });
  }

  ngOnInit(): void {
    this.init();
  }

  openLockerDetails(locker: CBox_GetAdminLockerListDataItem): void {
    this.scrollPosition.set(window.scrollY);
    window.scrollTo(0, 0);
    const dialog = this.dialog.open(CBoxAdminLockerInfoComponent, {
      data: locker.externalIdentifier,
      minWidth: "min(1200px, 100%)",
      panelClass: "mat-resize-dialog-container",
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      this.fetchData();
      window.scrollTo(0, this.scrollPosition());
    });
  }

  confirmLockerCreate(): void {
    const dialog = this.dialog.open(CBoxAdminLockerCreateConfirmDialogComponent, {
      width: "min(600px, 100%)",
      disableClose: true,
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      this.fetchData();
    });
  }

  editLocker(locker: CBox_AdminLockerData | undefined): void {
    if (!locker) {
      return;
    }
    const dialog = this.dialog.open(CBoxAdminLockerConfigDialogComponent, {
      data: locker,
      width: "min(600px, 100%)",
      disableClose: true,
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      this.fetchData();
    });
  }

  async init(): Promise<void> {
    this.lockers.set(await this.lockersService.getList());
    this.autoRefresh.valueChanges.pipe(
      tap(() => {
        clearInterval(this.refreshInterval);
      }),
      filter((autoRefresh) => !!autoRefresh),
    ).subscribe(() => {
      this.refreshInterval = setInterval(() => {
        console.log("RUNNING INTERVAL");
        this.loading.set(true);
        this.fetchData();
      }, 15000);
    })
  }

  formatDate(date: string): string {
    return formatDate(date, "dd-MM-YYYY HH:mm", "ro-RO", "Europe/Bucharest");
  }

  formatBytes(bytes: number): string {
    return formatBytes(bytes);
  }

  formatTimeInMilliseconds(ms: number): string {
    return formatTimeInMilliseconds(ms);
  }

  getDifferenceInMinutes(ms: number): number {
    return Math.floor(ms / 60000);
  }

  hasMinutesPassedSince(date: Date | undefined, minutes: number): boolean {
    if (!date) {
      return false;
    }
    return hasMinutesPassedSince(date, minutes);
  }

  calculatePercentage(value: number, total: number): number {
    if (total === 0) {
      return 0;
    }

    return (value / total) * 100;
  }

  getHashMatchValue(
      locker: CBox_GetAdminLockerListDataItem,
      type: CBox_GetAdminLockerListDataItem["state"]["hashes"][number]["type"]):
      CBox_AdminLockerData["state"]["hashes"][number]["matchState"] {
    return locker.state.hashes.find(h => h.type === type)?.matchState!;
  }

  getSearchStructure() {
    return Object.entries({
      "lockerIdentifiers[]": this.filtersForm.value.lockerIdentifiers,
      isActive: !!this.filtersForm.value.isActive,
      isConfigured: !!this.filtersForm.value.isConfigured,
      page: +this.filtersForm.value.page,
      pageSize: +this.filtersForm.value.pageSize
    })
    .filter(([_, value]) => value?.length || !!value)
    .reduce((acc, [key, value]) => {
      acc[key as keyof CBox_GetLockerListDataParams] = value;
      return acc;
    }, {} as CBox_GetLockerListDataParams);
  }

  override peekData(data: CBox_GetAdminLockerListDataItem[]): void {
    for (const locker of data) {
      const isOldHeartbeat = this.hasMinutesPassedSince(locker.heartbeat?.lastPingAt, 2);
      const memoryFreePercentage = this.calculatePercentage(locker?.heartbeat?.data?.memory?.available || 0, locker?.heartbeat?.data?.memory?.total || 1);
      const storageFreePercentage = this.calculatePercentage(locker?.heartbeat?.data?.storage?.available || 0, locker?.heartbeat?.data?.storage?.total || 1);
      this.lockerExtras.update((extras) => {
        extras[locker.externalIdentifier] = {
          isOldHeartbeat,
          memoryFreePercentage,
          storageFreePercentage
        };

        return extras;
      })
    }
  }

  isEmptyObject(obj: Record<string, any>): boolean {
    return Object.keys(obj).length === 0;
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
  }
}